import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ConversationPanel = ({ conversations, onRespond }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState({});

  const handleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSelectResponse = (conversationId, responseId) => {
    setSelectedResponse({ ...selectedResponse, [conversationId]: responseId });
  };

  const handleSendResponse = (conversationId) => {
    const responseId = selectedResponse?.[conversationId];
    if (responseId) {
      onRespond(conversationId, responseId);
      setSelectedResponse({ ...selectedResponse, [conversationId]: null });
      setExpandedId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'accent';
      case 'active':
        return 'primary';
      case 'resolved':
        return 'success';
      default:
        return 'muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return 'Sparkles';
      case 'active':
        return 'MessageCircle';
      case 'resolved':
        return 'CheckCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-therapeutic">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md">
            <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
              AI-Mediated Conversations
            </h2>
            <p className="text-sm md:text-base text-muted-foreground font-caption">
              {conversations?.length} active discussion{conversations?.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-3 md:space-y-4">
        {conversations?.map((conversation) => (
          <div
            key={conversation?.id}
            className="border border-border rounded-lg overflow-hidden transition-therapeutic hover:shadow-therapeutic"
          >
            <button
              onClick={() => handleExpand(conversation?.id)}
              className="w-full p-4 md:p-5 flex items-start justify-between bg-muted/30 hover:bg-muted/50 transition-therapeutic"
            >
              <div className="flex items-start space-x-3 md:space-x-4 flex-1">
                <div className="relative flex-shrink-0">
                  <Image
                    src={conversation?.aiAvatar}
                    alt={conversation?.aiAvatarAlt}
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                  />
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-${getStatusColor(conversation?.status)} rounded-full border-2 border-card flex items-center justify-center`}>
                    <Icon name={getStatusIcon(conversation?.status)} size={10} color="white" />
                  </div>
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="text-sm md:text-base lg:text-lg font-heading font-semibold text-foreground">
                      {conversation?.topic}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-caption font-medium bg-${getStatusColor(conversation?.status)}/10 text-${getStatusColor(conversation?.status)}`}>
                      {conversation?.status}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 font-caption">
                    {conversation?.preview}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-xs text-muted-foreground font-caption">
                      {conversation?.messageCount} messages
                    </span>
                    <span className="text-xs text-muted-foreground font-caption">
                      {conversation?.lastUpdate}
                    </span>
                  </div>
                </div>
              </div>
              <Icon 
                name={expandedId === conversation?.id ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                color="var(--color-muted-foreground)"
                className="flex-shrink-0 ml-2"
              />
            </button>

            {expandedId === conversation?.id && (
              <div className="p-4 md:p-5 bg-background border-t border-border">
                <div className="space-y-4 md:space-y-5">
                  <div className="bg-primary/5 border border-primary/20 rounded-md p-3 md:p-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="Sparkles" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm md:text-base text-foreground font-medium mb-2">
                          AI Insight
                        </p>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                          {conversation?.aiInsight}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm md:text-base font-medium text-foreground mb-3">
                      Suggested Responses
                    </p>
                    <div className="space-y-2 md:space-y-3">
                      {conversation?.suggestedResponses?.map((response) => (
                        <button
                          key={response?.id}
                          onClick={() => handleSelectResponse(conversation?.id, response?.id)}
                          className={`
                            w-full p-3 md:p-4 rounded-md border-2 text-left transition-therapeutic
                            ${selectedResponse?.[conversation?.id] === response?.id
                              ? 'border-primary bg-primary/10' :'border-border bg-muted/30 hover:bg-muted/50'
                            }
                          `}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`
                              flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 mt-0.5
                              flex items-center justify-center transition-therapeutic
                              ${selectedResponse?.[conversation?.id] === response?.id
                                ? 'border-primary bg-primary' :'border-border bg-background'
                              }
                            `}>
                              {selectedResponse?.[conversation?.id] === response?.id && (
                                <Icon name="Check" size={14} color="white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm md:text-base text-foreground leading-relaxed">
                                {response?.text}
                              </p>
                              <div className="flex items-center space-x-2 mt-2">
                                <span className={`text-xs font-caption px-2 py-0.5 rounded-full bg-${response?.tone === 'supportive' ? 'success' : response?.tone === 'curious' ? 'primary' : 'accent'}/10 text-${response?.tone === 'supportive' ? 'success' : response?.tone === 'curious' ? 'primary' : 'accent'}`}>
                                  {response?.tone}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-2">
                    <Button
                      variant="default"
                      fullWidth
                      onClick={() => handleSendResponse(conversation?.id)}
                      disabled={!selectedResponse?.[conversation?.id]}
                      iconName="Send"
                      iconPosition="right"
                    >
                      Send Response
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={() => setExpandedId(null)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationPanel;